import { AgXToneMapping, PCFShadowMap, SRGBColorSpace, WebGPURenderer } from 'three/webgpu';

import type { BackendName } from '../backend.js';

/** Section 4: the render target is the viewport times DPR, capped at 3840x2160. */
export const MAX_BUFFER_WIDTH = 3840;
export const MAX_BUFFER_HEIGHT = 2160;

export interface RendererHandle {
  renderer: WebGPURenderer;
  backend: BackendName;
  /** GPU timestamp queries are available (WebGPU `timestamp-query`, WebGL disjoint timer). */
  gpuTiming: boolean;
  maxAnisotropy: number;
  resize(cssWidth: number, cssHeight: number, devicePixelRatio: number): void;
}

interface BackendFlags {
  isWebGPUBackend?: boolean;
  trackTimestamp?: boolean;
}

/**
 * Creates and initialises the renderer with an explicit backend so Three's own fallback path
 * (which logs a console warning) never runs. `antialias: false` because TAAU replaces MSAA.
 */
export async function createRenderer(
  canvas: HTMLCanvasElement,
  forceWebGL: boolean,
  signal: AbortSignal,
): Promise<RendererHandle> {
  const renderer = new WebGPURenderer({
    canvas,
    antialias: false,
    forceWebGL,
    trackTimestamp: true,
  });
  renderer.toneMapping = AgXToneMapping;
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFShadowMap;
  await renderer.init();
  if (signal.aborted) {
    // The r186 runtime returns a promise although the 0.185 typings say void; wait either way.
    await Promise.resolve(renderer.dispose() as unknown).catch(() => undefined);
    signal.throwIfAborted();
  }
  const flags = renderer.backend as unknown as BackendFlags;
  return {
    renderer,
    backend: flags.isWebGPUBackend === true ? 'webgpu' : 'webgl2',
    gpuTiming: flags.trackTimestamp === true,
    maxAnisotropy: renderer.getMaxAnisotropy(),
    resize(cssWidth, cssHeight, devicePixelRatio) {
      const w = Math.max(1, cssWidth);
      const h = Math.max(1, cssHeight);
      const ratio = Math.min(devicePixelRatio, MAX_BUFFER_WIDTH / w, MAX_BUFFER_HEIGHT / h);
      renderer.setPixelRatio(ratio);
      renderer.setSize(w, h, false);
    },
  };
}
